import '../cookies/cookies.js';  // Cookie extensions
import * as DOM from '../dom/dom.js';  // DOM manipulation
import * as Ecosystem from '../ecosystem/ecosystem';  // Ecosystem class

// Login form
var form = null;

// Form fields
var email = null;
var password = null;
var dontexpire = null;
var submit = null;
var message = null;

// General "server error" message
var serverErrorMessage = 'Error contacting the server, please try again later.';

// Lock variables
var lockInterval = null;
var lockCookie = 'login-lock';

/**
 * Setup the DOM for the login form
 * @param  {string}  apiUrl  The URL of the API server
 */
export function setup(apiUrl)
{
	// Add a "login" class to the body
	document.body.classList.add('login');

	// Create the login form
	document.body.appendChild(_addForm(apiUrl));

	// Timeout to allow DOM creation
	setTimeout(() =>
	{
		_checkLock();
		_addOnSubmit();
	}, 1);
}

/**
 * Add the submit event to the form
 */
function _addOnSubmit()
{
	form.addEventListener('submit', e =>
	{
		e.preventDefault();

		// Clean error message
		message.toggleError(false);

		// Collect form data
		var formData = new FormData(form);
		formData.append('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone);

		// FormData → plain object
		const data = Object.fromEntries(formData.entries());

		// Call the server
		fetch(form.action, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(data),
		})

			// Parse JSON; if body is not JSON (e.g. 404, 500) fall through to catch
			.then(response => response.json()
				.then(data => data)
				.catch(() => Promise.reject(response))
			)

			// Handle API response
			.then(response =>
			{
				if (!response.success)
				{
					// Show error message
					message.toggleError(response.message);

					// Check "too many attempts" block
					if (response.data && response.data.blocked)
					{
						let now = Math.floor(Date.now() / 1000);
						Cookies.set(lockCookie, response.message, response.data.blocked - now);
						_checkLock();
					}
					return;
				}

				// Success — save token
				Cookies.set(Ecosystem.tokenName, response.data.token, response.data.token_exp, { domain: response.data.token_domain, path: '/', secure: true, samesite: 'none' });

				// Redirect - if there is a "u" querystring, use that, otherwise use the home URL
				var qs = new URLSearchParams(window.location.search);
				document.location = qs.get('u') ? qs.get('u') : response.data.home;
			})

			// Handle communication errors (network failure, invalid JSON, etc.)
			.catch(error =>
			{
				console.error('Login ::', error);
				message.toggleError(serverErrorMessage);
			});
	});
}

/**
 * Create the login form
 * @param    {string}  apiUrl  The URL of the API server
 * @returns  {object}          The login form
 */
function _addForm(apiUrl)
{
	// Create the form itself
	form = DOM.create('form', { 'id': 'loginForm', 'action': apiUrl + '/v1/auth/login', class: 'full' });

	// Add toggle method to form
	form.toggle = mode => form.querySelectorAll('fieldset').forEach(el => { el.disabled = !mode; })

	// Append elements to form
	form.appendChild(_addEmail());
	form.appendChild(_addPassword());
	form.appendChild(_addExpireFlag());
	form.appendChild(_addSubmit());
	form.appendChild(_addMessage());

	// Return the form
	return form;
}

/**
 * Add email field
 * @returns  {object}  The email fieldset
 */
function _addEmail()
{
	// Create email field
	email = DOM.create('input', {
		'type': 'email',
		'id': 'email',
		'name': 'email',
		'placeholder': 'your@email.com',
		'autocomplete': 'email',
		'required': ''
	});

	// Create email fieldset
	var fieldset = DOM.create('fieldset');
	fieldset.appendChild(DOM.create('label', { 'for': 'email' }, {}, 'E-mail:'));
	fieldset.appendChild(email);
	return fieldset;
};

/**
 * Add password field
 * @returns  {object}  The password fieldset
 */
function _addPassword()
{
	// Create password field
	password = DOM.create('input', {
		'type': 'password',
		'id': 'password',
		'name': 'password',
		'placeholder': '********',
		'autocomplete': 'current-password',
		'required': ''
	});

	// Create password fieldset
	var fieldset = DOM.create('fieldset');
	fieldset.appendChild(DOM.create('label', { 'for': 'password' }, {}, 'Password:'));
	fieldset.appendChild(password);
	return fieldset;
};

/**
 * Add "don't expire" flag
 * @returns  {object}  The "don't expire" fieldset
 */
function _addExpireFlag()
{
	// Create dontexpire field
	dontexpire = DOM.create('input', {
		'type': 'checkbox',
		'id': 'dontexpire',
		'name': 'dontexpire',
		'value': '1'
	});

	// Create dontexpire fieldset
	var fieldset = DOM.create('fieldset');
	fieldset.appendChild(dontexpire);
	fieldset.appendChild(DOM.create('label', { 'for': 'dontexpire' }, {}, 'Keep this session open'));
	return fieldset;
};

/**
 * Add submit button
 * @returns  {object}  The submit button fieldset
 */
function _addSubmit()
{
	// Create submit button
	submit = DOM.create('input', {
		'type': 'submit',
		'class': 'secure clickme',
		'value': 'Enter'
	});

	// Create submit fieldset
	var fieldset = DOM.create('fieldset', { 'class': 'buttons' });
	fieldset.appendChild(submit);
	return fieldset;
};

/**
 * Add message field
 * @returns  {object}  The message div
 */
function _addMessage()
{
	// Create the message field
	message = DOM.create('div', { 'id': 'loginMessage' });

	// Add toggle method to message
	message.toggleError = msg =>
	{
		message.innerText = msg ? msg : '';
		if (msg) message.style.display = 'block';
	}

	// Show error coming from querystring, if there
	var qs = new URLSearchParams(window.location.search);
	message.toggleError(qs.get('m'));

	// Return the message field
	return message;
}

/**
 * Checks if the login form is locked due to too many failed attempts.
 * Disables or enables the form and displays the lock message accordingly.
 */
function _checkLock()
{
	// Get "lock" cookie
	var lock = Cookies.get(lockCookie);

	// Is there a lock?
	if (lock)
	{
		// Still no interval?
		if (!lockInterval)
		{
			form.toggle(false);                             // Disable form
			message.toggleError(lock);                      // Show error message
			lockInterval = setInterval(_checkLock, 15000);  // Start check interval (every 15 seconds)
		}
	}

	// Is there NOT a lock?
	else
	{
		// Is there still an interval?
		if (lockInterval)
		{
			form.toggle(true);            // Enable form
			message.toggleError(false);   // Hide error message
			clearInterval(lockInterval);  // Stop check interval
			lockInterval = null;          // Clear interval variable
		}
	}
}